import type { ChatSubagentCard } from "../ui-types.ts";
import { resolveSubagentActivityPhase, type SubagentActivityPhase } from "./subagent-run-status.ts";

/**
 * Pillar A — Session-level status, derived client-side from the
 * subagent registry + connection state + last error.
 *
 * Distinct from `ChatSubagentStatus` (per-run lifecycle:
 * queued | spawning | running | done | error) and from
 * `ChatSubagentWorkStatus` (per-agent roster work status). This union
 * describes the *chat session* as a whole: is anything happening here,
 * is something broken, is the gateway reachable?
 *
 * Lives in the catalog so the sidebar can render one chip per row
 * without re-deriving the union inline. Pure module — depends only on
 * `ChatSubagentCard[]` so it is unit-testable without a renderer.
 *
 * UX-PLAN Pillar A — Session is a first-class object (Gap 1).
 */

export type SessionStatus = "running" | "idle" | "failed" | "blocked" | "offline";

export type SessionHealthInput = {
  /** Subagent cards for THIS session, as observed on the client. */
  subagents: readonly ChatSubagentCard[];
  /**
   * Whether the row's underlying session row came back with
   * `abortedLastRun: true` from the gateway (the last assistant run
   * did not finish cleanly). Optional — older sessions never carry it.
   */
  abortedLastRun?: boolean | null;
  /**
   * Optional per-session last-error text. Pillar B moves this to a
   * proper `Map<sessionKey, SessionHealth>`; until then we accept a
   * free-form string so Pillar A is shippable without server changes.
   */
  lastError?: string | null;
  /**
   * Whether the gateway WebSocket is currently connected. When `false`
   * the row is "offline" regardless of what the subagent cards say —
   * we cannot trust any "running" hint we observed before the drop.
   */
  connected: boolean;
};

/**
 * Map a `SessionStatus` to the `.status-dot--*` modifier class used by
 * the existing status-dot primitive in components.css. Keeps the
 * catalog free of CSS knowledge — the sidebar applies the class.
 */
export function sessionStatusDotClass(status: SessionStatus): string {
  switch (status) {
    case "running":
      return "running";
    case "idle":
      return "idle";
    case "failed":
      return "error";
    case "blocked":
      return "blocked";
    case "offline":
      return "offline";
    default:
      return "idle";
  }
}

/** Human label for the chip — short, screen-reader friendly. */
export function sessionStatusLabel(status: SessionStatus): string {
  switch (status) {
    case "running":
      return "Running";
    case "idle":
      return "Idle";
    case "failed":
      return "Failed";
    case "blocked":
      return "Blocked";
    case "offline":
      return "Offline";
    default:
      return "Idle";
  }
}

/**
 * Count how many subagent cards are in the "ongoing" phase (queued,
 * spawning, running). Exposed so the sidebar can render the
 * `N running` chip and so tests can pin the derivation.
 */
export function countOngoingSubagents(cards: readonly ChatSubagentCard[]): number {
  let n = 0;
  for (const card of cards) {
    if (resolveSubagentActivityPhase(card) === "ongoing") {
      n += 1;
    }
  }
  return n;
}

/**
 * Count subagent cards that ended in a failure-like phase: failed,
 * defunct, or cancelled. Exposed so Pillar H can surface the same
 * number in the parent thread without re-deriving.
 */
export function countFailedSubagents(cards: readonly ChatSubagentCard[]): number {
  let n = 0;
  for (const card of cards) {
    const phase = resolveSubagentActivityPhase(card);
    if (phase === "failed" || phase === "defunct" || phase === "cancelled") {
      n += 1;
    }
  }
  return n;
}

/**
 * Pure derivation of the session-level status.
 *
 * Precedence:
 *  1. `connected === false`                              → `"offline"`
 *     (we cannot trust any "running" hint we cached before the drop)
 *  2. `lastError` present                                → `"failed"`
 *  3. any subagent in `"blocked"` phase                  → `"blocked"`
 *  4. any subagent in `"ongoing"` phase                  → `"running"`
 *  5. any subagent in `"failed"`/`"defunct"`/`"cancelled"` → `"failed"`
 *  6. `abortedLastRun === true`                          → `"failed"`
 *  7. otherwise                                          → `"idle"`
 *
 * The phases come from `resolveSubagentActivityPhase` so the
 * classification matches the Fleet Inspector / subagent cards 1:1.
 */
export function computeSessionStatus(input: SessionHealthInput): SessionStatus {
  if (!input.connected) {
    return "offline";
  }
  if (input.lastError && input.lastError.trim().length > 0) {
    return "failed";
  }
  let hasOngoing = false;
  let hasBlocked = false;
  let hasFailed = false;
  for (const card of input.subagents) {
    const phase: SubagentActivityPhase = resolveSubagentActivityPhase(card);
    if (phase === "ongoing") {
      hasOngoing = true;
    } else if (phase === "blocked") {
      hasBlocked = true;
    } else if (phase === "failed" || phase === "defunct" || phase === "cancelled") {
      hasFailed = true;
    }
  }
  if (hasBlocked) {
    return "blocked";
  }
  if (hasOngoing) {
    return "running";
  }
  if (hasFailed) {
    return "failed";
  }
  if (input.abortedLastRun === true) {
    return "failed";
  }
  return "idle";
}
